import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlarmClock, Copy, Download, Eye, FileSignature, FolderInput, Lock, Unlock, MoreVertical, Pencil, Printer, RefreshCw, Share2, Trash2, UserPlus } from 'lucide-react';

interface ItemActionsProps {
  onToggleLock?: () => void;
  isLocked?: boolean;
  /**
   * Opens a fresh compose screen carrying this document's content. Nothing is
   * copied into storage: the new send gets its own number and signing link, so
   * the menu says what it does rather than calling it a copy.
   */
  onSendToAnotherSigner?: () => void;
  /**
   * 1.16 — a new one that starts from this one, for someone else: the quote for
   * another customer. Its own item and its own icon, not the share one: a copy
   * hands nothing on until it is sent. `duplicateLabel` is the screen's words.
   */
  onDuplicate?: () => void;
  duplicateLabel?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  onDownload?: () => void;
  onPrint?: () => void;
  onResend?: () => void;
  resendCount?: number;
  /**
   * What the resend item says instead of "send again". A draft has never gone
   * out, so the menu on one offers a send — the same action, named for what it
   * is about to do rather than for what it has never done.
   */
  resendLabel?: string;
  onView?: () => void;
  viewLabel?: string;
  /**
   * Hands this one row on — the same send screen the selection bar opens, for
   * a single item. It lives in here rather than on the row because a row that
   * carries its own icons has only as much width as the shortest of them
   * leaves, and this is the second icon that wanted the spot.
   */
  onShare?: () => void;
  /** What the share item says, when the screen has a better word than "share". */
  shareLabel?: string;
  /** LIB — put a stored document out for signature, from the row it sits on. */
  onSign?: () => void;
  /** 1.45 — file a stored document into one of the folders, or take it out. */
  onMove?: () => void;
  /** REM-007 — push the next alert without moving the reporting due date. */
  onSnooze?: () => void;
}

/** The per-row overflow menu. Rendered in a portal so a table's scroll box cannot clip it. */
export function ItemActions({
  onToggleLock,
  isLocked,
  onSendToAnotherSigner,
  onDuplicate,
  duplicateLabel,
  onEdit,
  onDelete,
  deleteLabel,
  onDownload,
  onPrint,
  onResend,
  resendCount,
  resendLabel,
  onView,
  viewLabel,
  onShare,
  shareLabel,
  onSign,
  onMove,
  onSnooze,
}: ItemActionsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const menuWidth = 200;
      const menuHeight = menuRef.current?.getBoundingClientRect().height ?? 200;
      const viewportPadding = 12;
      const nextLeft = Math.min(
        Math.max(rect.right - menuWidth, viewportPadding),
        window.innerWidth - menuWidth - viewportPadding,
      );
      // Flip above the row when it sits near the bottom of the viewport.
      const showAbove = rect.bottom + menuHeight > window.innerHeight - viewportPadding;
      setPosition({
        top: Math.max(viewportPadding, showAbove ? rect.top - menuHeight - 6 : rect.bottom + 6),
        left: nextLeft,
      });
    };

    /*
     * Both the row and the menu count as inside, and the menu needs saying out
     * loud: it is rendered in a portal, so it is not a DOM descendant of `ref`.
     * A press on one of its items therefore read as a press outside, which
     * unmounted the button before the click that follows the press could reach
     * it — every item in every list did nothing at all, download included.
     *
     * `pointerdown` rather than `mousedown` so a tap closes the menu when the
     * finger lands, instead of waiting for the emulated mouse event a phone
     * only sends once the finger lifts.
     */
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        ref.current?.querySelector('button')?.focus();
      }
    };

    if (open) {
      updatePosition();
      document.addEventListener('pointerdown', handler);
      document.addEventListener('keydown', escape);
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }

    return () => {
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const item = 'w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-start';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((p) => !p);
        }}
        aria-label={t('common.actions')}
        aria-expanded={open}
        className="p-1.5 rounded-lg hover:bg-muted transition-colors"
      >
        <MoreVertical className="h-4 w-4 text-muted-foreground" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] w-[200px] max-h-[calc(100dvh-24px)] overflow-y-auto rounded-lg border border-border bg-card py-1 shadow-xl"
            style={{ top: position.top, left: position.left }}
          >
            {onView && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onView();
                }}
                className={item}
              >
                <Eye className="h-3.5 w-3.5 text-primary" />
                {viewLabel ?? t('shell.view')}
              </button>
            )}
            {onShare && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onShare();
                }}
                className={`${item} text-primary`}
              >
                <Share2 className="h-3.5 w-3.5" />
                {shareLabel ?? t('common.share')}
              </button>
            )}
            {onSign && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onSign();
                }}
                className={`${item} text-primary`}
              >
                <FileSignature className="h-3.5 w-3.5" />
                {t('library.sendToSign')}
              </button>
            )}
            {onMove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onMove();
                }}
                className={item}
              >
                <FolderInput className="h-3.5 w-3.5 text-primary" />
                {t('library.moveToFolder')}
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onEdit();
                }}
                className={item}
              >
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                {t('common.edit')}
              </button>
            )}
            {onSnooze && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onSnooze();
                }}
                className={item}
              >
                <AlarmClock className="h-3.5 w-3.5 text-muted-foreground" />
                {t('reminders.snoozeDay')}
              </button>
            )}
            {onResend && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onResend();
                }}
                className={`${item} text-info`}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {resendLabel ?? ((resendCount ?? 0) > 0 ? t('shell.resendCount', { count: resendCount }) : t('shell.resend'))}
              </button>
            )}
            {onPrint && <button type="button" onClick={(event) => { event.stopPropagation(); setOpen(false); onPrint(); }} className={item}><Printer className="h-3.5 w-3.5 text-muted-foreground" />{t('common.print')}</button>}
            {onDownload && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onDownload();
                }}
                className={item}
              >
                <Download className="h-3.5 w-3.5 text-muted-foreground" />
                {t('common.download')}
              </button>
            )}
            {onSendToAnotherSigner && (
              <button type="button" className={item} onClick={(event) => {
                event.stopPropagation(); setOpen(false); onSendToAnotherSigner();
              }}>
                <UserPlus className="h-3.5 w-3.5" />{t('history.sendToAnotherSigner')}
              </button>
            )}
            {onDuplicate && (
              <button type="button" className={item} onClick={(event) => {
                event.stopPropagation(); setOpen(false); onDuplicate();
              }}>
                <Copy className="h-3.5 w-3.5" />{duplicateLabel ?? t('common.copy')}
              </button>
            )}
            {onToggleLock && (
              <button type="button" className={`${item} ${isLocked ? 'text-primary' : 'text-destructive'}`} onClick={(event) => {
                event.stopPropagation(); setOpen(false); onToggleLock();
              }}>
                {isLocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                {t(isLocked ? 'history.unlock' : 'history.lock')}
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onDelete();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-destructive/10 transition-colors text-destructive text-start"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {deleteLabel ?? t('common.delete')}
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
